import {
  useState, createContext, useMemo, PropsWithChildren,
  useContext,
} from 'react';
import { decodeToken } from 'react-jwt';
import axios from 'axios';
import useLocalStorageListener from '../../util/useLocalStorageListener';
import {
  LocalStorageBoolean, useDelayCall, useIsMsalAuth,
} from '../../..';

type SessionContextType = {
  exp: number,
  setSessionExpTime: (t: string) => void,
  refreshToken: () => void,
  /**
   * Indicates if the user is logged out by session timeout or manually logout.
   * This is used to prevent the user from being called refresh API
   * when they are already logged out.
   */
  getIsLoggedOut: () => boolean,
  setIsLoggedOut: (value: boolean) => void,
}

const defaultSessionContext: SessionContextType = {
  exp: 0,
  setSessionExpTime: () => { },
  refreshToken: () => { },
  getIsLoggedOut: () => false,
  setIsLoggedOut: () => { },
};

export const isLoggedOutKey = 'isLoggedOut';
const setIsLoggedOut = (value: boolean = false) => LocalStorageBoolean.set(isLoggedOutKey, value);
const getIsLoggedOut = () => LocalStorageBoolean.get(isLoggedOutKey);

const tokenExpirationTimeKey = 'tokenExpirationTime';
const setTokenExpirationTime = (value: number = 0) => localStorage.setItem(tokenExpirationTimeKey, value.toString());

export const SessionContext = createContext<SessionContextType>(defaultSessionContext);

const getExpTime = (token: string) => decodeToken<{ exp: number }>(token)?.exp ?? 0;

export function useSessionContext() {
  return useContext(SessionContext);
}

const refreshTokenApi = async (): Promise<string> => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_BROKER_MANAGEMENT_API_AUTH_PATH}/user/refresh-token`, {}, { withCredentials: true });
    return response?.data?.token || '';
  } catch (e) {
    return '';
  }
};

export function SessionProvider({ children }: PropsWithChildren) {
  const [exp, setExp] = useState<number>(0);
  const [refreshTokenCount, setRefreshTokenCount] = useState(0);

  useLocalStorageListener(tokenExpirationTimeKey, (newValue) => {
    const expTime = Number(newValue);
    if (expTime >= 0) {
      setExp(expTime);
    }
  });

  const isExternalUser = !useIsMsalAuth();

  const setSessionExpTime = (token: string) => {
    const expTime = getExpTime(token);
    setExp(expTime);
    setTokenExpirationTime(expTime);
  };

  useDelayCall(() => {
    if (isExternalUser) {
      refreshTokenApi().then((token) => {
        setSessionExpTime(token);
      });
    }
  }, [refreshTokenCount]);

  const refreshToken = () => {
    setRefreshTokenCount((pre) => pre + 1);
  };

  const sessionValue = useMemo(() => ({
    exp, setSessionExpTime, refreshToken, setIsLoggedOut, getIsLoggedOut,
  }), [exp, setSessionExpTime, refreshToken]);

  return (
    <SessionContext.Provider value={sessionValue}>
      {children}
    </SessionContext.Provider>
  );
}
